import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { createIncident } from 'api/canvms/incidents';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import type { AttentionEvent, IncidentSeverity } from '../types/canvision.types';

const SEVERITY_OPTIONS: { value: IncidentSeverity; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export interface PromoteEventDialogProps {
  open: boolean;
  onClose: (result?: { promoted: true; eventId: number }) => void;
  event: AttentionEvent | null;
  cameraName: string;
  siteName: string;
  siteId: number;
  cameraId: number;
}

/**
 * PromoteEventDialog
 *
 * Quick confirmation dialog to promote an attention-feed event into a formal
 * incident.  Pre-fills type/severity from the event; operator can adjust
 * before confirming.
 *
 * On confirm the dialog:
 *   1. POST /incidents (via createIncident) with event_id linking
 *      — backend automatically marks the source event as 'promoted'
 *   2. Calls onClose({ promoted: true, eventId }) so the parent can
 *      remove the event from its local list.
 */
function PromoteEventDialog({
  open,
  onClose,
  event,
  cameraName,
  siteName,
  siteId,
  cameraId,
}: PromoteEventDialogProps) {
  const [incidentType, setIncidentType] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('high');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { notifySuccess, notifyError } = useNotificationProvider();

  // Reset / pre-fill form when dialog opens with a new event
  useEffect(() => {
    if (open && event) {
      setIncidentType(event.eventType?.replace(/_/g, ' ') || '');
      setSeverity(event.severity || 'high');
      setNote('');
      setSubmitting(false);
    }
  }, [open, event]);

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!event) return;

    try {
      setSubmitting(true);

      // 1. Create the incident
      await createIncident({
        cameraId,
        siteId,
        incidentType: incidentType.trim() || event.eventType,
        severity,
        title: `${incidentType.trim() || event.eventType} — ${cameraName}`,
        description: note.trim() || undefined,
        eventId: event.eventId,
      });

      // 2. Toast success
      const displayType = incidentType.trim() || event.eventType?.replace(/_/g, ' ') || 'Event';
      notifySuccess(`Incident created — ${displayType} at ${cameraName}`);

      // 3. Close with promoted result so parent can remove event from list
      onClose({ promoted: true, eventId: event.eventId });
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create incident';
      console.error('[CANVMS/PromoteEventDialog] Error promoting event:', err);
      notifyError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Promote to Incident</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Type */}
          <TextField
            label="Type"
            fullWidth
            value={incidentType}
            onChange={(e) => setIncidentType(e.target.value)}
            size="small"
          />

          {/* Severity */}
          <FormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              label="Severity"
              onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Note */}
          <TextField
            label="Note"
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional one-line note"
            size="small"
          />

          {/* Read-only camera + site */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Camera:</strong> {cameraName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Site:</strong> {siteName}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !incidentType.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {submitting ? 'Creating...' : 'Create Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PromoteEventDialog;
